/**
 * Flow routes that get the town-skyline decoration (`.scene`/`.town` in
 * app.component.html), matched as URL substrings — which is how this check has
 * always worked, and why 'tenant' catches `pma-tenant-onboarding`.
 *
 * Three of these match no live URL and are kept only because removing them is
 * a behaviour question, not a cleanup: 'residential' is redirect-only (that
 * route now sends you to /renters), 'point-of-lease' never appears in a URL
 * because that module is routed under `program` with `opt-in`/`opt-out`
 * children, and 'deposit-choice' exists nowhere else in src/. So the live
 * matches are 'tenant' and 'upload-coverage-proof'.
 *
 * The consequence is worth knowing before extending this: the renters quote
 * flow (/renters, /renters/checkout/:id) and the lease pages (/program/opt-in)
 * show NO skyline, while every static content page now does. Making those
 * flows match means adding 'renters'/'program' here — a change to the main
 * funnel, deliberately not made as a side effect of the static-page work.
 */
const SKYLINE_FLOW_FRAGMENTS = [
  'residential',
  'upload-coverage-proof',
  'tenant',
  'deposit-choice',
  'point-of-lease',
];

/**
 * Static content pages that also get the skyline: it is the app's page-bottom
 * furniture and these pages looked bare without it, being the same shape as a
 * flow screen — one card on the page gradient.
 *
 * Matched on the FIRST PATH SEGMENT, not as a substring like the flow list
 * above. A bare `includes('faq')` would also fire on any route whose token or
 * query string happened to contain those letters; the flow fragments are long
 * enough for that to be unlikely, 'faq' is not. Casing is deliberate and
 * matches the route table (`Terms-of-Use`, `About-Us`, `Contact-Us`).
 *
 * NOT the portal (`/user`), which is a working surface rather than a landing
 * page.
 *
 * A page that opts in must establish a stacking context above the skyline:
 * `.scene` is `position: fixed; z-index: 0`, and a POSITIONED element at
 * z-index 0 paints above the inline content of NON-positioned ones, so the
 * buildings show straight through unpositioned card text. `.pma-flow`,
 * `flow-footnote`, `.new-faq` and the static-page card frame all carry
 * `position: relative` plus a z-index for this reason.
 *
 * Note this is NOT gated on iframe mode. Embedded surfaces already show the
 * skyline on flow routes (only the footer is behind `!iframeMode`), so the
 * static pages behave the same way rather than differently.
 */
const SKYLINE_STATIC_ROUTES = [
  'privacy-policy',
  'Terms-of-Use',
  'licenses',
  'faq',
  'About-Us',
  'Contact-Us',
  'giveback',
];

export function shouldShowSkyline(url: string): boolean {
  if (SKYLINE_FLOW_FRAGMENTS.some(fragment => url.includes(fragment))) {
    return true;
  }
  const firstSegment = url.split(/[?#]/)[0].split('/').filter(Boolean)[0];
  return SKYLINE_STATIC_ROUTES.includes(firstSegment);
}
